import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusIcon, ArrowRightIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import modelService from '../services/modelService';
import adminService from '../services/adminService';

// EL TAULER.
//
// ── Per què no hi ha comptadors (27/08/2026) ────────────────────────────────────────────────
//
// N'hi havia quatre: total de models, i quants de cada tipus. Cap dels quatre responia una
// pregunta que ningú es fes. **«Tinc tres models de bucles causals» no és informació**: és un
// recompte del teu propi calaix, i el calaix ja el tens a la pantalla del costat. Un comptador
// val la pena quan el número et fa fer alguna cosa diferent, i aquests no en feien fer cap.
//
// Abans d'això encara era pitjor: fins al 25/08/2026 els números eren **12, 48, 7 i 3 escrits al
// codi** i sota hi havia activitat inventada amb noms de models que no existien.
//
// El que sí que respon una pregunta real —«on era?»— és el model que has tocat últim. Per això
// el tauler és **un llançador**: et torna on eres amb un clic i et treu del davant tota la resta.
//
// ── I per què l'instructor en té un altre ───────────────────────────────────────────────────
//
// Perquè a ell sí que li serveix un número, i és d'una altra mena: **sobre persones, no sobre
// fitxers**. «Quatre encara no han començat» és una cosa que es pot fer dilluns al matí. «Set
// models de bucles causals» no ho és mai. La franja de classe va a sobre, i a sota hi té el
// mateix tauler que tothom amb els seus propis models: un instructor també modela.

const fa = (data, t) => {
  const s = Math.max(0, (Date.now() - new Date(data).getTime()) / 1000);
  if (s < 90) return t('time.now');
  if (s < 3600) return t('time.minutes', { n: Math.round(s / 60) });
  if (s < 86400) return t('time.hours', { n: Math.round(s / 3600) });
  return t('time.days', { n: Math.round(s / 86400) });
};

// LA FRANJA DE L'ADMINISTRADOR.
//
// **No és la de l'instructor, i el 27/08/2026 ho eren.** Es van decidir totes dues en una sola
// pregunta —«l'instructor i l'admin»— i el resultat era que un administrador veia el tauler d'un
// alumne, perquè la franja de classe només es dibuixa si tens alumnes assignats i un admin no en
// té: **té el sistema sencer**.
//
// El que li serveix és una altra cosa: **què hi ha per atendre**. Comptes esperant aprovació,
// peticions de contrasenya, i —la que costa més de veure i més mal fa— **alumnes sense instructor
// assignat**: mentre no en tinguin, el seu docent no veu res de la seva feina, i no ho sabrà
// ningú perquè no falla res. La regla de visibilitat penja d'un camp que ha d'omplir algú.
function FranjaAdmin({ cua, usuaris, models, jo, t }) {
  const sensePare = usuaris.filter(
    (u) => u.role === 'modeler' && !u.instructorId && u.approvalStatus === 'approved');
  const noms = new Map(usuaris.map((u) => [u.id, u.name]));
  // Els moviments són **dels altres**: el que has fet tu ja el tens a sota, al teu propi tauler.
  // El recompte de comptes, en canvi, et compta a tu: és quanta gent hi ha al sistema.
  const dAltri = models.filter((m) => noms.has(m.creatorId) && m.creatorId !== jo);

  const pendents = [
    cua?.comptes ? { clau: 'tauler.aprovaComptes', n: cua.comptes } : null,
    cua?.contrasenyes ? { clau: 'tauler.peticionsContrasenya', n: cua.contrasenyes } : null,
    sensePare.length ? { clau: 'tauler.senseInstructor', n: sensePare.length,
                         nota: 'tauler.senseInstructorNota' } : null,
  ].filter(Boolean);

  return (
    <div className="mb-8 bg-white border border-gray-200 rounded-md overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span className="text-sm text-gray-800">
          <b className="tabular-nums">{usuaris.length}</b> {t('tauler.comptes')}
        </span>
        <span className="text-sm text-gray-800">
          <b className="tabular-nums">{usuaris.filter((u) => u.role === 'instructor').length}</b>{' '}
          {t('tauler.instructors')}
        </span>
        <Link to="/admin" className="ml-auto text-xs text-gray-500 hover:text-gray-700">
          {t('tauler.gestionaComptes')}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            {t('tauler.perAtendre')}
          </h3>
          {!pendents.length
            ? <p className="text-sm text-gray-600">{t('tauler.resPerAtendre')}</p>
            : (
              <ul className="space-y-2">
                {pendents.map((p) => (
                  <li key={p.clau}>
                    <Link to="/admin" className="text-sm text-gray-900 hover:text-primary-800">
                      <b className="tabular-nums">{p.n}</b> {t(p.clau, { count: p.n })}
                    </Link>
                    {p.nota && <p className="text-xs text-gray-500 mt-0.5">{t(p.nota)}</p>}
                  </li>
                ))}
              </ul>
            )}
        </div>

        <div className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            {t('tauler.ultimsMoviments')}
          </h3>
          {!dAltri.length
            ? <p className="text-sm text-gray-600">{t('tauler.capMoviment')}</p>
            : (
              <ul className="space-y-1.5">
                {dAltri.slice(0, 4).map((m) => (
                  <li key={m.id} className="text-sm flex items-baseline gap-2">
                    <span className="text-gray-800 font-medium flex-shrink-0">{noms.get(m.creatorId)}</span>
                    <Link to={`/models/${m.id}`} className="text-gray-600 hover:text-gray-900 truncate">
                      {m.name}
                    </Link>
                    <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
                      {fa(m.updatedAt || m.createdAt, t)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  );
}

// LA FRANJA DE CLASSE.
//
// Tot el que diu surt de dues crides que ja es fan: els teus alumnes i els models que veus —que
// per a un instructor inclouen els dels seus alumnes, per la regla de visibilitat—. No hi ha cap
// endpoint d'estadístiques al darrere, i és a posta: mentre el que es vol saber es pugui comptar
// aquí, un endpoint nou només seria una còpia més de la mateixa regla per mantenir sincronitzada.
function FranjaClasse({ alumnes, models, t }) {
  const ids = new Set(alumnes.map((a) => a.id));
  const seus = models.filter((m) => ids.has(m.creatorId));
  const ambFeina = new Set(seus.map((m) => m.creatorId));
  const sense = alumnes.filter((a) => !ambFeina.has(a.id));
  const noms = new Map(alumnes.map((a) => [a.id, a.name]));

  // Un instructor sense alumnes assignats **també ho ha de saber**: si no, se li ensenya el
  // tauler d'un alumne i no entén per què no veu la seva classe. És el mateix defecte que feia
  // que un administrador veiés el tauler d'un alumne.
  if (!alumnes.length) {
    return (
      <div className="mb-8 bg-white border border-gray-200 rounded-md px-5 py-4">
        <p className="text-sm text-gray-800">{t('tauler.capAlumneAssignat')}</p>
        <p className="text-sm text-gray-600 mt-1">{t('tauler.capAlumneAssignatCos')}</p>
      </div>
    );
  }

  return (
    <div className="mb-8 bg-white border border-gray-200 rounded-md overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span className="text-sm text-gray-800">
          <b className="tabular-nums">{alumnes.length}</b> {t('tauler.alumnes')}
        </span>
        <span className="text-sm text-gray-800">
          <b className="tabular-nums">{ambFeina.size}</b> {t('tauler.hanComencat')}
        </span>
        {sense.length > 0 && (
          <span className="text-sm text-warn-900">
            <b className="tabular-nums">{sense.length}</b> {t('tauler.encaraNo')}
          </span>
        )}
        <Link to="/admin" className="ml-auto text-xs text-gray-500 hover:text-gray-700">
          {t('tauler.gestionaComptes')}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            {t('tauler.senseComencar')}
          </h3>
          {!sense.length
            ? <p className="text-sm text-gray-600">{t('tauler.totsHanComencat')}</p>
            : <p className="text-sm text-gray-800 leading-relaxed">
                {sense.map((a) => a.name).join(' · ')}
              </p>}
        </div>

        <div className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            {t('tauler.ultimsMoviments')}
          </h3>
          {!seus.length
            ? <p className="text-sm text-gray-600">{t('tauler.capMoviment')}</p>
            : (
              <ul className="space-y-1.5">
                {seus.slice(0, 4).map((m) => (
                  <li key={m.id} className="text-sm flex items-baseline gap-2">
                    <span className="text-gray-800 font-medium flex-shrink-0">
                      {noms.get(m.creatorId)}
                    </span>
                    <Link to={`/models/${m.id}`} className="text-gray-600 hover:text-gray-900 truncate">
                      {m.name}
                    </Link>
                    <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
                      {fa(m.updatedAt || m.createdAt, t)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [models, setModels] = useState(null);
  const [alumnes, setAlumnes] = useState([]);
  const [error, setError] = useState(false);

  const [cua, setCua] = useState(null);
  const [usuaris, setUsuaris] = useState([]);

  // Tres taulers i no dos: l'alumne continua on era; l'instructor hi té a sobre la seva classe;
  // i l'administrador, què hi ha per atendre. Un admin no té alumnes —té el sistema sencer—, i
  // ensenyar-li la franja de classe li deixava el tauler d'un alumne.
  const esAdmin = user?.role === 'admin';
  const esInstructor = user?.role === 'instructor';

  useEffect(() => {
    modelService.getModels({ limit: 100 })
      .then((d) => setModels(d.models || []))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (esInstructor) modelService.elsMeusAlumnes().then(setAlumnes).catch(() => setAlumnes([]));
  }, [esInstructor]);

  useEffect(() => {
    if (!esAdmin) return;
    adminService.summary().then(setCua).catch(() => setCua(null));
    adminService.users().then((d) => setUsuaris(d.users || [])).catch(() => setUsuaris([]));
  }, [esAdmin]);

  // Els que són **teus**: els que has creat i els que t'han assignat. Per a un instructor la
  // llista que arriba porta també els models dels seus alumnes, i aquells no són la seva feina.
  // El servidor els torna ordenats per data de modificació, o sigui que el primer és l'últim
  // que s'ha tocat i no cal tornar-los a ordenar.
  const meus = (models || []).filter(
    (m) => m.creatorId === user?.id || (m.sharedWith || []).includes(user?.id));
  const continua = meus[0];
  const resta = meus.slice(1, 7);

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard.welcome', { name: user?.name })}
          </h1>
          <p className="mt-2 text-gray-600">{t('dashboard.subtitle')}</p>
        </div>
        <Link to="/models?nou=1"
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-700 text-white rounded-md
                     text-sm font-semibold hover:bg-primary-800 flex-shrink-0">
          <PlusIcon className="h-5 w-5" />
          {t('tauler.modelNou')}
        </Link>
      </div>

      {error && (
        <div className="mb-6 p-3 bg-danger-50 border border-danger-100 text-danger-800 rounded-md text-sm">
          {t('dashboard.loadFailed')}
        </div>
      )}

      {esAdmin && models !== null && (
        <FranjaAdmin cua={cua} usuaris={usuaris} models={models} jo={user?.id} t={t} />
      )}
      {esInstructor && models !== null && (
        <FranjaClasse alumnes={alumnes} models={models} t={t} />
      )}

      {models === null && !error && (
        <p className="text-sm text-gray-500">{t('dashboard.loading')}</p>
      )}

      {/* CONTINUA. La targeta gran, i l'única cosa que el tauler demana que miris. */}
      {continua && (
        <Link to={`/models/${continua.id}`}
          className="block mb-8 bg-white border border-gray-200 rounded-md p-6
                     hover:border-primary-300 hover:shadow-sm transition-all group">
          <div className="text-xs uppercase tracking-wider text-gray-500">{t('tauler.continua')}</div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-2xl font-semibold text-gray-900">{continua.name}</h2>
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
              {t(`modelTypes.${continua.type}`)}
            </span>
          </div>
          {continua.description && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">{continua.description}</p>
          )}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {t('dashboard.updated', { when: fa(continua.updatedAt || continua.createdAt, t) })}
            </span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-primary-700
                             group-hover:gap-2.5 transition-all">
              {t('tauler.obre')} <ArrowRightIcon className="h-4 w-4" />
            </span>
          </div>
        </Link>
      )}

      {/* Qui encara no té res: què és això i per on comença. Un tauler buit hauria de dir què
          s'hi fa, no ensenyar quatre zeros. */}
      {models !== null && !meus.length && (
        <div className="bg-white border border-dashed border-gray-300 rounded-md p-10 text-center">
          <Square3Stack3DIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-800 font-medium">{t('tauler.buitTitol')}</p>
          <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">{t('tauler.buitCos')}</p>
          <Link to="/models?nou=1"
            className="inline-flex items-center gap-2 mt-5 px-4 py-2.5 bg-primary-700 text-white
                       rounded-md text-sm font-semibold hover:bg-primary-800">
            <PlusIcon className="h-5 w-5" />
            {t('tauler.modelNou')}
          </Link>
        </div>
      )}

      {resta.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-xs uppercase tracking-wider text-gray-500">
              {t('tauler.elsTeusModels', { n: meus.length })}
            </h3>
            <Link to="/models" className="text-sm text-gray-500 hover:text-gray-700">
              {t('tauler.veureLsTots')}
            </Link>
          </div>
          <ul className="bg-white border border-gray-200 rounded-md divide-y divide-gray-100">
            {resta.map((m) => (
              <li key={m.id}>
                <Link to={`/models/${m.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">{m.name}</span>
                    <span className="block text-xs text-gray-500">{t(`modelTypes.${m.type}`)}</span>
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {fa(m.updatedAt || m.createdAt, t)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
